/**
 * Apple In-App Purchase Service
 * Handles StoreKit 2 subscription purchases, validation, and restoration
 */

import { supabase } from '../lib/supabaseClient';

// Product IDs - MUST match App Store Connect exactly
export const PRODUCT_IDS = {
  STANDARD: 'com.noorcad.app.standard.month',
  PRO_TRIAL: 'com.noorcad.app.trial.month', // Free 14-day trial
  PRO: 'com.noorcad.app.pro.month',
  TEAM: 'com.noorcad.app.team.month',
  BUSINESS: 'com.noorcad.app.business.month',
  TEAM_MEMBER_SLOT: 'com.noorcad.team.slot', // £59/month - 1 additional team member
  TEAM_MEMBER_SLOT_2: 'com.noorcad.team.slot.2', // £100/month - 2 additional team members
  TEAM_MEMBER_SLOT_5: 'com.noorcad.team.slot.5', // £500/month - 5 additional team members
} as const;

export type ProductId = typeof PRODUCT_IDS[keyof typeof PRODUCT_IDS];

export interface IAPProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  currency: string;
  priceMicros: number;
}

export interface PurchaseResult {
  success: boolean;
  productId?: string;
  transactionId?: string;
  receipt?: string;
  error?: string;
}

export interface SubscriptionStatus {
  isActive: boolean;
  productId?: string;
  expirationDate?: Date;
  isInTrialPeriod?: boolean;
}

declare global {
  interface Window {
    CdvPurchase?: {
      store: any;
      ProductType: any;
      Platform: any;
      LogLevel: any;
    };
  }
}

class IAPService {
  private store: any = null;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  /**
   * Initialize the IAP store
   */
  async initialize(): Promise<void> {
    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Return immediately if already initialized
    if (this.isInitialized) {
      return;
    }

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  private async _performInitialization(): Promise<void> {
    try {
      console.log('[IAP] Initializing In-App Purchase service...');

      // Check if CdvPurchase is available (Cordova plugin)
      if (!window.CdvPurchase) {
        console.warn('[IAP] CdvPurchase not available - likely running in browser');
        return;
      }

      this.store = window.CdvPurchase.store;
      const { ProductType, Platform, LogLevel } = window.CdvPurchase;

      // Enable verbose logging in development
      if (process.env.NODE_ENV === 'development') {
        this.store.verbosity = LogLevel.DEBUG;
      }

      // Register products
      console.log('[IAP] Registering products...');
      this.store.register([
        {
          id: PRODUCT_IDS.STANDARD,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.PRO_TRIAL,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.PRO,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.TEAM,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.BUSINESS,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.TEAM_MEMBER_SLOT,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.TEAM_MEMBER_SLOT_2,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: PRODUCT_IDS.TEAM_MEMBER_SLOT_5,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
      ]);

      // Set up event handlers
      this._setupEventHandlers();

      // Initialize the store
      await this.store.initialize([Platform.APPLE_APPSTORE]);
      
      this.isInitialized = true;
      console.log('[IAP] ✅ In-App Purchase service initialized');
    } catch (error) {
      console.error('[IAP] ❌ Failed to initialize IAP:', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Set up purchase event handlers
   */
  private _setupEventHandlers(): void {
    if (!this.store) return;

    // Handle approved purchases - validate receipt server-side
    this.store.when().approved(async (transaction: any) => {
      console.log('[IAP] Purchase approved:', transaction.productId, 'Has receipt:', !!transaction.receipt);
      
      try {
        // Skip validation if no receipt (happens with owned products on app launch)
        if (!transaction.receipt) {
          console.log('[IAP] No receipt - finishing transaction immediately (existing purchase)');
          transaction.finish();
          return;
        }
        
        // Validate receipt with our server
        const isValid = await this.validateReceipt(transaction.receipt);
        
        if (isValid) {
          // Finish the transaction
          transaction.finish();
          console.log('[IAP] ✅ Transaction finished:', transaction.productId);
        } else {
          console.error('[IAP] ❌ Receipt validation failed');
        }
      } catch (error) {
        console.error('[IAP] Error validating receipt:', error);
        // Still finish transaction to prevent blocking
        transaction.finish();
      }
    });

    // Handle verified purchases - sync to Supabase
    this.store.when().verified(async (receipt: any) => {
      console.log('[IAP] Receipt verified:', receipt);
      
      try {
        await this.syncSubscriptionToSupabase(receipt);
      } catch (error) {
        console.error('[IAP] Error syncing to Supabase:', error);
      }
    });

    // Handle purchase errors - global error handler
    this.store.error((error: any) => {
      console.error('[IAP] Purchase error:', error);
    });
  }

  /**
   * Get available products from App Store
   */
  async getProducts(): Promise<IAPProduct[]> {
    await this.initialize();
    
    if (!this.store) {
      console.warn('[IAP] Store not available');
      return [];
    }

    try {
      console.log('[IAP] Fetching products...');
      
      const products = Object.values(PRODUCT_IDS).map(productId => {
        const product = this.store.get(productId);
        if (!product) return null;

        return {
          id: product.id,
          title: product.title || '',
          description: product.description || '',
          price: product.pricing?.price || '0',
          currency: product.pricing?.currency || 'GBP',
          priceMicros: product.pricing?.priceMicros || 0,
        };
      }).filter(Boolean) as IAPProduct[];

      console.log('[IAP] ✅ Products loaded:', products.length);
      return products;
    } catch (error) {
      console.error('[IAP] Failed to load products:', error);
      return [];
    }
  }

  /**
   * Purchase a subscription
   */
  async purchase(productId: ProductId): Promise<PurchaseResult> {
    await this.initialize();
    
    if (!this.store) {
      return {
        success: false,
        error: 'Store not available',
      };
    }

    try {
      console.log('[IAP] Starting purchase:', productId);
      
      const offer = this.store.get(productId)?.getOffer();
      if (!offer) {
        throw new Error(`Product ${productId} not found`);
      }

      // Order the product
      const result = await offer.order();
      
      console.log('[IAP] Purchase initiated:', result);
      
      return {
        success: true,
        productId,
      };
    } catch (error: any) {
      console.error('[IAP] Purchase failed:', error);
      return {
        success: false,
        error: error.message || 'Purchase failed',
      };
    }
  }

  /**
   * Restore previous purchases
   */
  async restorePurchases(): Promise<boolean> {
    await this.initialize();
    
    if (!this.store) {
      console.warn('[IAP] Store not available');
      return false;
    }

    try {
      console.log('[IAP] Restoring purchases...');
      
      await this.store.restorePurchases();
      
      console.log('[IAP] ✅ Purchases restored');
      return true;
    } catch (error) {
      console.error('[IAP] Failed to restore purchases:', error);
      return false;
    }
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    await this.initialize();
    
    if (!this.store) {
      return { isActive: false };
    }

    try {
      // Check all subscription products
      for (const productId of Object.values(PRODUCT_IDS)) {
        const product = this.store.get(productId);
        
        if (product?.owned) {
          const expiryDate = product.expiryDate ? new Date(product.expiryDate) : undefined;
          const isActive = expiryDate ? expiryDate > new Date() : true;
          
          return {
            isActive,
            productId,
            expirationDate: expiryDate,
            isInTrialPeriod: product.isInTrialPeriod || false,
          };
        }
      }
      
      return { isActive: false };
    } catch (error) {
      console.error('[IAP] Error checking subscription status:', error);
      return { isActive: false };
    }
  }

  /**
   * Validate receipt with Apple server
   * This should be done server-side for security
   */
  private async validateReceipt(receiptData: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[IAP] No user logged in');
        return false;
      }

      console.log('[IAP] Validating receipt with server...');
      
      // Call your server endpoint to validate with Apple
      const response = await fetch('/api/iap/validate-receipt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receipt: receiptData,
          userId: user.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Receipt validation failed');
      }

      const result = await response.json();
      return result.valid === true;
    } catch (error) {
      console.error('[IAP] Receipt validation error:', error);
      return false;
    }
  }

  /**
   * Sync subscription to Supabase after successful purchase
   */
  private async syncSubscriptionToSupabase(receipt: any): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[IAP] No user logged in');
        return;
      }

      const productId = receipt.productId;
      
      // Map product ID to plan type
      let planType = 'standard';
      if (productId.includes('pro')) planType = 'pro';
      else if (productId.includes('team')) planType = 'team';
      else if (productId.includes('business')) planType = 'business';

      console.log('[IAP] Syncing subscription to Supabase:', { userId: user.id, planType });

      // Update user metadata with subscription plan
      const { error: userError } = await supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          subscription_plan: planType,
          subscription_platform: 'apple_iap',
        },
      });

      if (userError) {
        console.error('[IAP] Failed to update user metadata:', userError);
      }

      // Update profile subscription
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          subscription_plan: planType,
          subscription_end_date: receipt.expiryDate || null,
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('[IAP] Failed to update profile:', profileError);
      }

      console.log('[IAP] ✅ Subscription synced to Supabase');
    } catch (error) {
      console.error('[IAP] Error syncing to Supabase:', error);
    }
  }

  /**
   * Open subscription management in iOS Settings
   */
  async manageSubscriptions(): Promise<void> {
    if (!this.store) {
      console.warn('[IAP] Store not available');
      return;
    }

    try {
      // Open iOS subscription management
      await this.store.manageSubscriptions();
    } catch (error) {
      console.error('[IAP] Failed to open subscription management:', error);
      // Fallback: Open App Store subscriptions page
      window.open('https://apps.apple.com/account/subscriptions', '_system');
    }
  }
}

// Export singleton instance
export const iapService = new IAPService();
